// Who can be reached on which channel, for the Email Customers modal.
//
// Every bucket is over ADDRESSABLE customers: sub-customers (rows with a
// parent_customer_id) are billing children of another customer and are never
// addressed on their own. Within a channel the buckets are DISJOINT and sum
// to the addressable total, so the owner never has to subtract two numbers to
// find out who is missing.
//
// EMAIL (unchanged from before texts existed): do not serve, then
// unsubscribed, then no email address, else can be emailed. A customer with
// no address who is also unsubscribed is counted once, as unsubscribed: the
// opt-out is the fact that has to stay visible (see EmailCustomersModal).
//
// TEXT, in the order POST /api/sms/send refuses them (smsSend.cannotTextReason):
// do not serve, opted out, no consent, no mobile number, else can be texted.
// Consent and the number are read from the customer row itself, which on an
// addressable (root) customer is where they live (migration 085).
//
// The server decides again on send, so these buckets are what the screen
// shows, not the guarantee.

export const CHANNELS = ['email', 'text', 'both'];

export const isDoNotServe = (c) => c.do_not_serve === true;

export function textReason(c) {
  if (isDoNotServe(c)) return 'do_not_serve';
  if (c.sms_consent === 'opted_out') return 'opted_out';
  if (c.sms_consent !== 'opted_in') return 'no_consent';
  if (!c.phone_e164) return 'no_phone';
  return null;
}

export const TEXT_REASON_LABELS = {
  do_not_serve: 'Do not serve',
  opted_out: 'Opted out of texts',
  no_consent: 'No text consent',
  no_phone: 'No mobile number',
  too_long: 'Text too long',
  customer_not_found: 'Customer removed',
  no_season_link: 'Not in the season',
  season_link_expired: 'Season link expired',
};

// The season filters (season confirmations): the customer's answer for the
// season the Dashboard tile shows, from GET /api/customers' season_status.
// A customer with no season row (lawn-only, Do Not Serve, added after Start)
// is in none of them.
export const SEASON_FILTERS = {
  season_pending: { status: 'pending', label: 'No reply this season' },
  season_confirmed: { status: 'confirmed', label: 'Confirmed this season' },
  season_declined: { status: 'declined', label: 'Declined this season' },
};

export function filterBySeason(customers, filterMode) {
  const f = SEASON_FILTERS[filterMode];
  if (!f) return customers;
  return customers.filter((c) => c.season_status === f.status);
}

/**
 * @param {object[]} customers the page's customer rows
 * @param {Set<number>} subscribedIds customers subscribed to email from this screen
 */
export function computeBuckets(customers, subscribedIds = new Set()) {
  const addressable = customers.filter((c) => !c.parent_customer_id);
  const isUnsubscribed = (c) => c.email_subscribed === false && !subscribedIds.has(c.id);
  const hasEmail = (c) => Boolean(c.email);

  const doNotServe = addressable.filter(isDoNotServe);
  const email = {
    eligible: addressable.filter((c) => !isDoNotServe(c) && !isUnsubscribed(c) && hasEmail(c)),
    unsubscribed: addressable.filter((c) => !isDoNotServe(c) && isUnsubscribed(c)),
    noEmail: addressable.filter((c) => !isDoNotServe(c) && !isUnsubscribed(c) && !hasEmail(c)),
  };
  const text = {
    eligible: addressable.filter((c) => textReason(c) === null),
    optedOut: addressable.filter((c) => textReason(c) === 'opted_out'),
    noConsent: addressable.filter((c) => textReason(c) === 'no_consent'),
    noPhone: addressable.filter((c) => textReason(c) === 'no_phone'),
  };
  return { addressable, doNotServe, email, text, isUnsubscribed, hasEmail };
}

/**
 * The base list a filter narrows, for a channel: who can be reached on it.
 * For 'both', anyone reachable on either, in page order.
 */
export function eligibleFor(channel, buckets) {
  if (channel === 'text') return buckets.text.eligible;
  if (channel === 'both') {
    const either = new Set([...buckets.email.eligible, ...buckets.text.eligible].map((c) => c.id));
    return buckets.addressable.filter((c) => either.has(c.id));
  }
  return buckets.email.eligible;
}

/**
 * The customers shown greyed in Manual selection, each with why.
 */
export function excludedFor(channel, buckets) {
  const emailReasons = [
    ...buckets.email.noEmail.map((c) => ({ customer: c, reason: 'No email address' })),
    ...buckets.email.unsubscribed.map((c) => ({ customer: c, reason: 'Unsubscribed' })),
    ...buckets.doNotServe.map((c) => ({ customer: c, reason: 'Do not serve' })),
  ];
  if (channel === 'email') return emailReasons;
  const textExcluded = buckets.addressable
    .filter((c) => textReason(c) !== null)
    .map((c) => ({ customer: c, reason: TEXT_REASON_LABELS[textReason(c)] }));
  if (channel === 'text') return textExcluded;
  // both: only those reachable on neither channel
  const reachable = new Set(eligibleFor('both', buckets).map((c) => c.id));
  return buckets.addressable
    .filter((c) => !reachable.has(c.id))
    .map((c) => ({ customer: c, reason: isDoNotServe(c) ? 'Do not serve' : 'No email or text' }));
}

// The send lists for a set of filtered recipients.
export function splitByChannel(channel, recipients, buckets) {
  const emailIds = new Set(buckets.email.eligible.map((c) => c.id));
  const textIds = new Set(buckets.text.eligible.map((c) => c.id));
  return {
    email: channel === 'text' ? [] : recipients.filter((c) => emailIds.has(c.id)),
    text: channel === 'email' ? [] : recipients.filter((c) => textIds.has(c.id)),
  };
}
